import { useEffect, useState, useCallback, useRef } from "react";
import { store as accBootStore } from "../../Lit/acc-boot/acc-boot.js";
import type { TreeNodeData } from "../TreeViewDataLayer";

interface UseInternationalFilteringProps {
  filterLevel2International: boolean;
  onInternationalChange?: () => void;
}

interface UseInternationalFilteringReturn {
  filterData: (data: TreeNodeData | null) => TreeNodeData | null;
  inInternational: boolean;
  hideInternationalDownlines: boolean;
}

/**
 * Hook to handle international ABO filtering based on acc-boot store state
 */
export const useInternationalFiltering = ({
  filterLevel2International,
  onInternationalChange,
}: UseInternationalFilteringProps): UseInternationalFilteringReturn => {
  // Seed from the store's current value (not a hardcoded default) so a fresh mount -
  // e.g. switching back to Tree View after changing a filter while it was unmounted -
  // reflects what's already in the store instead of waiting for the next setState to fire.

  const [inInternational, setInInternational] = useState<boolean | undefined>(
    () => (accBootStore as any).getState().inInternational ?? false,
  );
  const [hideInternationalDownlines, setHideInternationalDownlines] = useState<boolean | undefined>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => (accBootStore as any).getState().hideInternationalDownlines ?? true,
  );
  const isInitialMount = useRef(true);
  const onInternationalChangeRef = useRef(onInternationalChange);

  // Keep ref updated with latest callback
  useEffect(() => {
    onInternationalChangeRef.current = onInternationalChange;
  }, [onInternationalChange]);

  useEffect(() => {
    let isActive = true;
    const callback = (state: { inInternational?: boolean; hideInternationalDownlines?: boolean }) => {
      // Defer state updates to avoid "Cannot update a component while rendering
      // a different component" warnings when the store fires during another render.
      queueMicrotask(() => {
        if (!isActive) return;
        setInInternational(state.inInternational);
        setHideInternationalDownlines(state.hideInternationalDownlines);
      });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unsubscribe = (accBootStore as any).subscribe(callback) as (() => void) | void;
    return () => {
      isActive = false;
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onInternationalChangeRef.current?.();
  }, [inInternational, hideInternationalDownlines]);

  const filterData = useCallback(
    (data: TreeNodeData | null): TreeNodeData | null => {
      if (!data) return null;

      const filterNode = (node: TreeNodeData, filterFn: (child: TreeNodeData) => boolean): TreeNodeData => ({
        ...node,
        downlines: node.downlines?.filter(filterFn).map((child) => filterNode(child, filterFn)),
      });

      if (inInternational) {
        // Keep international ABOs at level 2 and ALL their downlines (don't filter downlines)
        const keepInternationalWithDownlines = (node: TreeNodeData): TreeNodeData => ({
          ...node,
          downlines: node.downlines
            ?.filter((child) => child.level === 2 && child.internationalFlag)
            .map((child) => child), // Keep all downlines of international ABOs as-is
        });
        return keepInternationalWithDownlines(data);
      }

      if (hideInternationalDownlines) {
        return filterNode(data, (child) => !child.internationalFlag);
      }

      if (filterLevel2International) {
        return filterNode(data, (child) => !(child.level === 2 && child.internationalFlag));
      }

      return data;
    },
    [inInternational, hideInternationalDownlines, filterLevel2International],
  );

  return {
    filterData,
    inInternational: inInternational ?? false,
    hideInternationalDownlines: hideInternationalDownlines ?? true,
  };
};
